//Node Modules
import { useEffect, useRef } from "react";
import { useSetRecoilState, useRecoilValue } from "recoil";
import _ from "lodash";

//GraphQL
import { API, graphqlOperation, Hub } from "aws-amplify";
import { CONNECTION_STATE_CHANGE, ConnectionState } from "@aws-amplify/pubsub";
import { onUpdateDeviceSensorAlertByScheme, onCreateDeviceSensorAlert } from "../../graphql/subscriptions-custom";
import { listAlarmAlerts } from "../../graphql/queries-custom";

//Pages

//BinaryForge Components

//3rd Party Components

//Atoms
import { deviceSensorAlarmListAtom } from "../../atoms/alarm";
import { selectedUserSchemeAtom } from "../../atoms/user";

//Helpers

//Other

//Polling safety net: subscriptions miss alerts written outside AppSync mutations (e.g. direct
//DynamoDB writes) and events lost during connection drops - a slow refetch caps staleness
const ALARM_POLL_INTERVAL_MS = 60000;

const AlarmSubscriber = () => {
	let updateDeviceSensorAlarmsubscription = null;
	let createDeviceSensorAlarmsubscription = null;

	//Recoil State
	const setDeviceSensorAlarmList = useSetRecoilState(deviceSensorAlarmListAtom);
	const selectedUserScheme = useRecoilValue(selectedUserSchemeAtom);

	const refreshInFlight = useRef(false);

	//Load Initial Data
	useEffect(() => {
		if (selectedUserScheme) {
			getInitialDeviceSensorAlerts();
		}
	}, [selectedUserScheme]);

	//Setup Subscriber
	useEffect(() => {
		if (selectedUserScheme) {
			updateDeviceSensorAlarmsubscription = subscribeDeviceSensorAlert();
			createDeviceSensorAlarmsubscription = subscribeDeviceSensorAlertCreate();
		}

		return () => {
			if (updateDeviceSensorAlarmsubscription?.unsubscribe) updateDeviceSensorAlarmsubscription.unsubscribe();
			if (createDeviceSensorAlarmsubscription?.unsubscribe) createDeviceSensorAlarmsubscription.unsubscribe();
		};
		// Subscribe once per scheme - re-subscribing on every list change loses events
		// that arrive during the teardown/setup gap (e.g. two alarms cleared in quick succession)
	}, [selectedUserScheme]);

	//Polling safety net
	useEffect(() => {
		if (!selectedUserScheme) return;

		const interval = setInterval(() => getInitialDeviceSensorAlerts(), ALARM_POLL_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [selectedUserScheme]);

	//Events missed while the websocket was down are lost - refetch when the connection recovers
	useEffect(() => {
		let lastState = null;
		const stopListening = Hub.listen("api", ({ payload }) => {
			if (payload.event !== CONNECTION_STATE_CHANGE) return;
			const state = payload.data.connectionState;
			if (state === ConnectionState.Connected && lastState && lastState !== ConnectionState.Connected) {
				getInitialDeviceSensorAlerts();
			}
			lastState = state;
		});

		return () => stopListening();
	}, [selectedUserScheme]);

	const isAlarmActive = (alarm) => Number(alarm?.sensor_state) > 0;

	const getInitialDeviceSensorAlerts = async () => {
		if (refreshInFlight.current) return;
		refreshInFlight.current = true;
		try {
			const {
				data: {
					deviceSensorAlertBySensorState: { items: sensorAlarmItems },
				},
			} = await API.graphql(
				graphqlOperation(listAlarmAlerts, {
					sensor_state: 1,
					filter: { schemeId: { eq: selectedUserScheme.id } },
				}),
			);

			// Skip alarms with no linked device (orphaned alerts whose device was deleted) - they cannot be rendered or counted
			const activeSensorAlarmItems = sensorAlarmItems.filter((alarm) => isAlarmActive(alarm) && alarm?.device);

			for await (const sensorAlarmItem of activeSensorAlarmItems) {
				const outputDevices = [];
				const alarmGroupInputs = sensorAlarmItem.device?.alarmGroupsInput?.items ?? [];

				for await (const alarmGroupInput of alarmGroupInputs) {
					const outputItems = alarmGroupInput?.alarmGroup?.output?.items ?? [];
					for await (const outputDevice of outputItems) {
						if (outputDevice?.device) outputDevices.push(outputDevice.device);
					}
				}

				sensorAlarmItem.outputDevices = _.uniqBy(outputDevices, "id");
			}

			//Keep the current array identity when nothing changed so downstream
			//refetch-on-atom-change effects do not churn on every poll
			const signature = (list) =>
				(list ?? []).map((alarm) => `${alarm.id}:${alarm.sensor_state}:${alarm.updatedAt}`).join("|");
			setDeviceSensorAlarmList((currentList) =>
				signature(currentList) === signature(activeSensorAlarmItems) ? currentList : activeSensorAlarmItems,
			);
		} catch (error) {
			console.warn("Alarm initial load failed", error);
		} finally {
			refreshInFlight.current = false;
		}
	};

	//Subscribe to updates
	const subscribeDeviceSensorAlert = () => {
		try {
			const sub = API.graphql(
				graphqlOperation(onUpdateDeviceSensorAlertByScheme, { schemeId: selectedUserScheme.id })
			).subscribe({
				next: ({
					value: {
						data: { onUpdateDeviceSensorAlertByScheme: updatedSensorAlert },
					},
				}) => {
					updateAlarmList(updatedSensorAlert);
				},
				error: (error) => console.warn("Alarm update subscription error", error),
			});

			return sub;
		} catch (error) {
			console.warn("Alarm update subscription failed", error);
			return null;
		}
	};

	const subscribeDeviceSensorAlertCreate = () => {
		try {
			const sub = API.graphql(
				graphqlOperation(onCreateDeviceSensorAlert, { filter: { schemeId: { eq: selectedUserScheme.id } } })
			).subscribe({
				next: ({
					value: {
						data: { onCreateDeviceSensorAlert: createdSensorAlert },
					},
				}) => {
					updateAlarmList(createdSensorAlert);
				},
				error: (error) => console.warn("Alarm create subscription error", error),
			});

			return sub;
		} catch (error) {
			console.warn("Alarm create subscription failed", error);
			return null;
		}
	};

	// Functional update so the long-lived subscription handlers never read a stale list
	const updateAlarmList = (updatedSensorAlert) => {
		//Some backend writers select narrow mutation result sets, so the subscription payload
		//arrives without device (or sensor_state) - refetch the full list rather than drop the event
		if (updatedSensorAlert?.sensor_state === null || (isAlarmActive(updatedSensorAlert) && !updatedSensorAlert.device)) {
			getInitialDeviceSensorAlerts();
			return;
		}

		setDeviceSensorAlarmList((currentList) => {
			const alarmExists = currentList.find((alarm) => alarm.id === updatedSensorAlert.id);

			if (isAlarmActive(updatedSensorAlert) && updatedSensorAlert.device && !alarmExists) {
				const outputDevices = [];
				const alarmGroupInputs = updatedSensorAlert.device?.alarmGroupsInput?.items ?? [];
				for (const alarmGroupInput of alarmGroupInputs) {
					const outputItems = alarmGroupInput?.alarmGroup?.output?.items ?? [];
					for (const outputDevice of outputItems) {
						if (outputDevice?.device) outputDevices.push(outputDevice.device);
					}
				}
				updatedSensorAlert.outputDevices = _.uniqBy(outputDevices, "id");

				return _.unionBy([updatedSensorAlert], currentList, "id");
			}

			if (!isAlarmActive(updatedSensorAlert) && alarmExists) {
				return _.filter(currentList, (a) => a.id !== updatedSensorAlert.id);
			}

			return currentList;
		});
	};

	return null;
};

export default AlarmSubscriber;
